import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useUIStore, type TimeFormat } from '@/core/stores/ui.store';
import { useUpdatePreferences } from '../../hooks/useUpdatePreferences';
import { FloatingLabelAsyncCombobox } from '@/shared/components/custom/async-combobox/FloatingLabelAsyncCombobox';
import type { AsyncComboboxOption } from '@/shared/components/custom/async-combobox/AsyncCombobox.types';

const TIME_FORMAT_OPTIONS: AsyncComboboxOption[] = [
  { id: 'HH:mm', label: '14:30', description: '24h sin segundos' },
  { id: 'HH:mm:ss', label: '14:30:45', description: '24h con segundos' },
  { id: 'hh:mm a', label: '2:30 PM', description: '12h sin segundos' },
  { id: 'hh:mm:ss a', label: '2:30:45 PM', description: '12h con segundos' },
];

interface TimeFormatSectionProps {
  showHeader?: boolean;
}

export function TimeFormatSection({ showHeader = true }: TimeFormatSectionProps) {
  const { t } = useTranslation('settings');
  const timeFormat = useUIStore((s) => s.timeFormat);
  const setTimeFormat = useUIStore((s) => s.setTimeFormat);
  const updatePreferences = useUpdatePreferences();

  const handleChange = (value: string | null) => {
    if (value) {
      setTimeFormat(value as TimeFormat);
      updatePreferences.mutate({ timeFormat: value as TimeFormat });
    }
  };

  return (
    <div className="space-y-3" data-testid="settings-timeformat-section">
      {showHeader && (
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('timeFormat.title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t('timeFormat.description')}</p>
        </div>
      )}

      <div className="space-y-3">
        <FloatingLabelAsyncCombobox
          id="settings-timeformat"
          label={t('timeFormat.title')}
          queryKey={['settings', 'timeFormats']}
          staticOptions={TIME_FORMAT_OPTIONS}
          value={timeFormat}
          onChange={handleChange}
          placeholder={t('timeFormat.placeholder')}
        />
        {updatePreferences.isPending && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3 h-3 animate-spin" />
            {t('preferences.saving')}
          </div>
        )}
      </div>
    </div>
  );
}
